/**
 * Web Push on this device (PWA lot 2) — browser side.
 *
 * Rules:
 *  - The notification permission is requested ONLY inside `enablePush()`, which the UI calls
 *    from a click. Nothing here prompts on load, on login or in the background.
 *  - No token or credential is stored: web storage only holds a random device id and one
 *    timestamp. The subscription itself stays in the browser's push manager.
 *  - On iPhone, push exists only in the installed app (added to the Home Screen, iOS 16.4+).
 *
 * Lot 4A — silent restoration, under strict conditions. When a person explicitly enables the
 * notifications, this device remembers it locally (`ridalo:pushOptIn` = { uid, prefs }: an account
 * id and the chosen categories, never a credential). If the server later lost the record because the
 * login session ended by itself (idle / absolute expiry), the next valid sign-in of the SAME account
 * re-registers the SAME browser subscription — only while the permission is still granted and the browser
 * still holds a subscription; the permission is never asked again, and the previously chosen categories
 * are kept. The marker is erased by a voluntary sign-out and by « Désactiver ». A marker of ANOTHER
 * account (or a subscription this account never enabled here) is dropped, so a phone never keeps
 * notifying a previous user.
 */
import axios from 'axios';
import { isIos, isStandalone } from './pwaInstall';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const DEVICE_KEY = 'ridalo:pushDeviceId';
const SEEN_KEY = 'ridalo:pushSeenAt';
export const OPTIN_KEY = 'ridalo:pushOptIn';
const CATEGORIES = ['new_booking', 'cancellation', 'message'];
const SEEN_EVERY_MS = 24 * 60 * 60 * 1000;

const read = (k) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* private mode */ } };
const remove = (k) => { try { window.localStorage.removeItem(k); } catch { /* private mode */ } };

const cleanPrefs = (prefs) => {
  if (!prefs || typeof prefs !== 'object') return null;
  const out = {};
  CATEGORIES.forEach((c) => { if (typeof prefs[c] === 'boolean') out[c] = prefs[c]; });
  return Object.keys(out).length ? out : null;
};

/** { uid, prefs } when THIS device was explicitly opted in by an account, else null. */
export const readOptIn = () => {
  try {
    const v = JSON.parse(read(OPTIN_KEY) || 'null');
    return v && typeof v.uid === 'string' && v.uid ? { uid: v.uid, prefs: cleanPrefs(v.prefs) } : null;
  } catch { return null; }
};
const writeOptIn = (uid, prefs) => { if (uid) write(OPTIN_KEY, JSON.stringify({ uid, prefs: cleanPrefs(prefs) })); };
export const clearOptIn = () => remove(OPTIN_KEY);

export const urlBase64ToUint8Array = (b64) => {
  const padded = (b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

/** Random id of this browser installation (not a credential). */
export const getDeviceId = () => {
  const saved = read(DEVICE_KEY);
  if (saved && /^[A-Za-z0-9_-]{16,64}$/.test(saved)) return saved;
  const c = window.crypto;
  if (!c || !c.getRandomValues) return null;
  const id = btoa(String.fromCharCode(...c.getRandomValues(new Uint8Array(16)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  write(DEVICE_KEY, id);
  return id;
};

/** Why push cannot work in this browser, or null when it can. */
export const unsupportedReason = ({ win = window, nav = navigator } = {}) => {
  if (isIos(nav) && !isStandalone(win)) return 'ios_install';
  if (!win.isSecureContext) return 'insecure';
  if (!('serviceWorker' in nav)) return 'no_service_worker';
  if (!('PushManager' in win)) return 'no_push';
  if (!('Notification' in win)) return 'no_notification';
  return null;
};

/** Same rule as the service worker and the server: only a Ridalo dashboard link. */
export const isSafeDashboardUrl = (url) => typeof url === 'string' && /^\/dashboard(\?[A-Za-z0-9=&_%.:-]{0,200})?$/.test(url);

export const getPermission = (win = window) => ('Notification' in win ? win.Notification.permission : 'unsupported');

export const fetchConfig = async () => (await axios.get(`${API}/push/config`, { withCredentials: true })).data;
export const listDevices = async () => (await axios.get(`${API}/push/devices`, { withCredentials: true })).data.devices;

const registration = async () => navigator.serviceWorker.ready;
export const currentSubscription = async () => {
  try { return await (await registration()).pushManager.getSubscription(); } catch { return null; }
};

/** Called from a click ONLY. Asks the permission, subscribes, registers this device. */
export async function enablePush({ language, userId } = {}) {
  const cfg = await fetchConfig();
  if (!cfg.available) return { status: 'unavailable', reason: cfg.reason };
  const permission = await window.Notification.requestPermission();      // the only place that asks
  if (permission !== 'granted') return { status: permission === 'denied' ? 'denied' : 'dismissed' };
  const reg = await registration();
  const sub = (await reg.pushManager.getSubscription())
    || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(cfg.public_key) }));
  // The marker is written ONLY once the server has accepted the registration: a failed registration (network,
  // 4xx, 5xx — the call throws before this point) leaves no marker, so nothing can ever be restored for a
  // subscription the server never recorded. A marker that already existed is left untouched.
  const { data } = await axios.post(`${API}/push/subscriptions`, {
    device_id: getDeviceId(), subscription: sub.toJSON(), consent: true, standalone: isStandalone(), language,
  }, { withCredentials: true });
  writeOptIn(userId, data && data.prefs);                                // the explicit « Activer » of this account, on this device
  write(SEEN_KEY, String(Date.now()));
  return { status: 'enabled', device: data };
}

/** Turn push off for this device: browser subscription AND server record. */
export async function disablePush() {
  clearOptIn();                                                          // a voluntary « Désactiver » is never undone silently
  const sub = await currentSubscription();
  try { if (sub) await sub.unsubscribe(); } catch { /* already gone */ }
  try { await axios.delete(`${API}/push/subscriptions/${getDeviceId()}`, { withCredentials: true }); } catch (e) {
    if (e?.response?.status !== 404) throw e;
  }
}

export const setPreferences = async (deviceId, prefs) => {
  const data = (await axios.put(`${API}/push/devices/${deviceId}/preferences`, prefs, { withCredentials: true })).data;
  const marker = readOptIn();
  if (marker) writeOptIn(marker.uid, data && data.prefs);                // remembered, so a restoration keeps the person's choices
  return data;
};

export const sendTest = async (language) =>
  (await axios.post(`${API}/push/test`, { device_id: getDeviceId(), language }, { withCredentials: true })).data;

/** On sign-out: drop this browser's subscription (the server also deletes it with the session). */
export async function unsubscribeLocal() {
  clearOptIn();                                                          // voluntary sign-out: nothing may be restored afterwards
  try {
    const sub = await Promise.race([currentSubscription(), new Promise((r) => setTimeout(() => r(null), 1500))]);
    if (sub) await sub.unsubscribe();
  } catch { /* best effort */ }
}

/** Drop this browser's subscription and the local marker (never the server record of another account). */
async function dropLocalSubscription() {
  clearOptIn();
  try {
    const sub = await currentSubscription();
    if (sub) await sub.unsubscribe();
  } catch { /* best effort: the push service rejects it on the next send and the server forgets it */ }
}

/**
 * Quiet maintenance when the app opens — NEVER prompts, NEVER acts before a valid sign-in:
 *  - `userId` is the signed-in account (PushRuntime only runs with one); any failure to read the server
 *    (signed out, offline, error) decides NOTHING;
 *  - permission withdrawn → remove the server record and the local marker;
 *  - subscription renewed by the browser (or missing while allowed) → re-register it;
 *  - at most once a day, refresh `last_seen`;
 *  - server record gone (session ended by itself, 404/410 after a renewal, lost device id) while THIS
 *    account had enabled the notifications here, the permission is still granted and the browser still has
 *    a subscription → restore it silently, with the categories the person had chosen;
 *  - a marker of ANOTHER account, or a subscription this account never enabled on this device → dropped.
 */
export async function reconcilePush({ language, userId } = {}) {
  if (unsupportedReason()) return 'unsupported';
  const id = getDeviceId();
  if (!id) return 'no_device_id';
  let cfg;
  try { cfg = await fetchConfig(); } catch { return 'offline'; }
  if (!cfg.available) return 'unavailable';
  let devices;
  try { devices = await listDevices(); } catch { return 'unknown'; }     // 401 / network / 5xx: never decide anything
  const mine = devices && devices.find((d) => d.device_id === id);
  const marker = readOptIn();
  const permission = getPermission();

  if (userId && marker && marker.uid !== userId) {                       // a previous account's leftovers on this phone
    await dropLocalSubscription();
    return 'foreign_cleared';
  }

  if (!mine) {
    const existing = await currentSubscription();
    if (!userId) return 'not_registered';
    if (marker && marker.uid === userId) {                               // this account enabled the notifications here
      if (permission !== 'granted') { clearOptIn(); return 'permission_withdrawn'; }
      if (!existing) return 'no_subscription';                           // nothing valid to give back: never create one
      try {
        const { data } = await axios.post(`${API}/push/subscriptions`, {
          device_id: id, subscription: existing.toJSON(), consent: true, standalone: isStandalone(), language,
          restored: true, ...(marker.prefs ? { prefs: marker.prefs } : {}),
        }, { withCredentials: true });
        writeOptIn(userId, data && data.prefs);
        write(SEEN_KEY, String(Date.now()));
        return 'restored';
      } catch { return 'restore_failed'; }
    }
    if (existing) { await dropLocalSubscription(); return 'foreign_cleared'; }   // not this account's: stop notifying it
    return 'not_registered';                                             // never opted in on this device
  }

  if (permission !== 'granted') {                                        // withdrawn in the system settings
    clearOptIn();
    try { await axios.delete(`${API}/push/subscriptions/${id}`, { withCredentials: true }); } catch { /* ignore */ }
    return 'removed';
  }
  if (userId && (!marker || JSON.stringify(marker.prefs) !== JSON.stringify(cleanPrefs(mine.prefs)))) {
    writeOptIn(userId, mine.prefs);                                      // keep the local copy of the choices current
  }
  let sub = await currentSubscription();
  if (!sub) {
    try {
      sub = await (await registration()).pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(cfg.public_key) });
    } catch { return 'resubscribe_failed'; }
  }
  const stale = Date.now() - Number(read(SEEN_KEY) || 0) > SEEN_EVERY_MS;
  const changed = !mine.endpoint_hint || !mine.endpoint_hint.endsWith(await shortHash(sub.endpoint));
  if (stale || changed) {
    await axios.post(`${API}/push/subscriptions`, { device_id: id, subscription: sub.toJSON(), consent: true, standalone: isStandalone(), language }, { withCredentials: true });
    write(SEEN_KEY, String(Date.now()));
    return 'refreshed';
  }
  return 'ok';
}

/** First 6 hex chars of SHA-256(endpoint): the server shows the same hint. */
export async function shortHash(text) {
  const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 6);
}

/** What the settings card shows. Pure, so it can be tested. */
export const describeState = ({ unsupported, config, permission, device }) => {
  if (unsupported) return unsupported === 'ios_install' ? 'ios_install' : 'unsupported';
  if (!config) return 'loading';
  if (!config.available) return 'unavailable';
  if (permission === 'denied') return 'denied';
  return device && permission === 'granted' ? 'on' : 'off';
};
